import { formatCodeableConcept, resolveId, type WithId } from '@medplum/core';
import type { Medication, MedicationRequest, MedicationStatement } from '@medplum/fhirtypes';

type MedicationDisplayRecord = WithId<MedicationRequest | MedicationStatement>;

export function getMedicationReferenceIdWithoutDisplay(record: MedicationDisplayRecord): string | undefined {
  if (record.medicationCodeableConcept || record.medicationReference?.display) {
    return undefined;
  }

  const reference = record.medicationReference?.reference;
  if (!reference?.startsWith('Medication/')) {
    return undefined;
  }

  return resolveId(record.medicationReference);
}

export function getMedicationReferenceIdsWithoutDisplay(records: MedicationDisplayRecord[]): string[] {
  return [...new Set(records.map(getMedicationReferenceIdWithoutDisplay).filter((id): id is string => Boolean(id)))];
}

export function hydrateMedicationReferenceDisplay<T extends MedicationDisplayRecord>(
  record: T,
  medicationsById: ReadonlyMap<string, Medication>,
): T {
  const medicationId = getMedicationReferenceIdWithoutDisplay(record);
  if (!medicationId) {
    return record;
  }

  const medication = medicationsById.get(medicationId);
  const display = medication ? formatCodeableConcept(medication.code) : undefined;
  if (!display) {
    return record;
  }

  return {
    ...record,
    medicationReference: {
      ...record.medicationReference,
      display,
    },
  };
}

export function hydrateMedicationReferenceDisplays<T extends MedicationDisplayRecord>(
  records: T[],
  medications: Medication[],
): T[] {
  const medicationsById = new Map(
    medications
      .filter((medication): medication is Medication & { id: string } => Boolean(medication.id))
      .map((medication) => [medication.id, medication] as const),
  );
  return records.map((record) => hydrateMedicationReferenceDisplay(record, medicationsById));
}
